import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthRetryService {
  private readonly MAX_CONSECUTIVE_FAILURES = 2;
  private consecutiveFailures = 0;
  private lastSuccessTime = Date.now();

  private isOnline$ = new BehaviorSubject<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  constructor() {
    // Monitorear estado de conectividad del navegador
    if (typeof window !== 'undefined') {
      merge(
        fromEvent(window, 'online').pipe(map(() => true)),
        fromEvent(window, 'offline').pipe(map(() => false))
      ).subscribe(isOnline => {
        this.isOnline$.next(isOnline);
        if (isOnline) {
          console.log('Conexión de red restaurada');
          this.resetFailureCount();
        } else {
          console.log('Conexión de red perdida');
        }
      });
    }
  }

  /**
   * Verifica si el dispositivo está online
   */
  isOnline(): boolean {
    return this.isOnline$.value;
  }

  /**
   * Observable del estado de conectividad
   */
  getOnlineStatus() {
    return this.isOnline$.asObservable();
  }

  /**
   * Registra un fallo en la obtención del token
   */
  recordFailure(): void {
    this.consecutiveFailures++;
    console.log(`Fallo en la obtención del token registrado. Fallos consecutivos: ${this.consecutiveFailures}`);
  }

  /**
   * Registra un éxito en la obtención del token
   */
  recordSuccess(): void {
    this.consecutiveFailures = 0;
    this.lastSuccessTime = Date.now();
    console.log('Fallo en la obtención del token registrado');
  }

  /**
   * Verifica si se ha alcanzado el límite de fallos consecutivos
   */
  hasReachedFailureLimit(): boolean {
    return this.consecutiveFailures >= this.MAX_CONSECUTIVE_FAILURES;
  }

  /**
   * Resetea el contador de fallos
   */
  resetFailureCount(): void {
    this.consecutiveFailures = 0;
  }

  /**
   * Calcula el tiempo de espera antes del próximo intento
   * Implementa backoff exponencial con jitter
   */
  getBackoffDelay(): number {
    if (!this.isOnline()) {
      return 30000; // 30 segundos si no hay conexión
    }

    const baseDelay = 1000; // 1 segundo base
    const maxDelay = 60000; // 60 segundos máximo
    const exponentialDelay = baseDelay * Math.pow(2, Math.min(this.consecutiveFailures, 6));
    const jitter = Math.random() * 1000; // Añadir algo de aleatoriedad

    return Math.min(exponentialDelay + jitter, maxDelay);
  }

  /**
   * Verifica si es recomendable intentar una nueva solicitud
   */
  shouldAttemptRetry(): boolean {
    // NOTA: navigator.onLine es poco fiable (falsos negativos con VPN/proxy) y
    // puede quedar obsoleto si no llega ningún evento online/offline. Por eso ya
    // no bloquea la solicitud: la ausencia real de red la manejan timeout/retry.
    if (this.hasReachedFailureLimit()) {
      const timeSinceLastSuccess = Date.now() - this.lastSuccessTime;
      const cooldownPeriod = 5 * 60 * 1000; // 5 minutos

      if (timeSinceLastSuccess < cooldownPeriod) {
        console.log('No se puede reintentar: demasiados fallos consecutivos, esperando enfriamiento');
        return false;
      }

      // Después del período de enfriamiento, resetear y permitir un intento
      this.resetFailureCount();
    }

    return true;
  }

  /**
   * Obtiene información del estado actual para debugging
   */
  getStatus() {
    return {
      isOnline: this.isOnline(),
      consecutiveFailures: this.consecutiveFailures,
      hasReachedLimit: this.hasReachedFailureLimit(),
      shouldRetry: this.shouldAttemptRetry(),
      nextBackoffDelay: this.getBackoffDelay(),
      lastSuccessTime: new Date(this.lastSuccessTime).toISOString()
    };
  }
}
